import { useState } from 'react'

import { errorMessage } from '@/shared/components/data'
import { Button, Field, Input, Select, Sheet } from '@/shared/components/ui'
import { useUserIndex } from '@/shared/hooks/use-users'
import { useT } from '@/shared/i18n'
import { slugify } from '@/shared/lib/format'
import type { CompanyDetail, CompanyStatus } from '@/shared/types/domain'

import { useCreateCompany, useUpdateCompany } from '../api'

export interface CompanyFormProps {
  open: boolean
  onClose: () => void
  onCreated?: (companyId: string) => void
  /**
   * La empresa a editar. Sin esto, el formulario da de alta.
   *
   * Es el mismo formulario y no dos: los campos son los mismos y duplicarlo
   * garantizaba que el día que se agregue uno quede en una sola de las dos
   * pantallas. Lo que cambia entre modos es puntual y está marcado abajo: el
   * slug no se edita (es la identidad de la empresa, y vive en URLs y en el
   * nombre del canal de Slack) y el estado sólo aparece al editar, porque una
   * empresa nace activa.
   */
  company?: CompanyDetail | null
}

/** Alta y edición de empresa. */
export function CompanyForm({ open, onClose, onCreated, company }: CompanyFormProps) {
  const t = useT()
  const editando = Boolean(company)
  const create = useCreateCompany()
  const update = useUpdateCompany(company?.id ?? '')
  const mutacion = editando ? update : create
  const users = useUserIndex()

  const [name, setName] = useState(company?.name ?? '')
  const [slug, setSlug] = useState(company?.slug ?? '')
  const [slugTocado, setSlugTocado] = useState(Boolean(company))
  const [ownerId, setOwnerId] = useState(company?.primary_tech_owner?.id ?? '')
  const [hue, setHue] = useState(String(company?.color_hue ?? 210))
  const [slack, setSlack] = useState(company?.slack_channel ?? '')
  const [status, setStatus] = useState(company?.status ?? 'active')

  if (!open) return null

  // El slug se deriva del nombre MIENTRAS nadie lo haya editado; a partir de
  // ahí manda lo escrito. Sin ese `slugTocado`, corregirlo era imposible: cada
  // tecla del nombre lo pisaba de nuevo.
  const slugFinal = slugTocado ? slug : slugify(name)
  const valid = name.trim().length > 0 && slugFinal.length > 0

  const submit = () => {
    if (company) {
      // `slug` NO viaja: el backend no lo acepta en el patch y cambiarlo
      // rompería los links compartidos de esa empresa.
      update.mutate(
        {
          name: name.trim(),
          status,
          primary_tech_owner_id: ownerId || null,
          color_hue: Number(hue),
          slack_channel_id: slack.trim() || null,
        },
        { onSuccess: () => onClose() },
      )
      return
    }

    create.mutate(
      {
        name: name.trim(),
        slug: slugFinal,
        primary_tech_owner_id: ownerId || null,
        color_hue: Number(hue),
        slack_channel_id: slack.trim() || null,
      },
      {
        onSuccess: (empresa) => {
          onCreated?.(empresa.id)
          onClose()
        },
      },
    )
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={
        <h2 className="text-base text-text-1">
          {editando ? t('companies.form.editTitle') : t('companies.form.title')}
        </h2>
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          {mutacion.isError ? (
            <span role="alert" className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
              {errorMessage(
                mutacion.error,
                t(editando ? 'companies.error.update' : 'companies.error.create'),
              )}
            </span>
          ) : (
            <span />
          )}
          <span className="flex gap-2">
            <Button onClick={onClose}>{t('common.action.cancel')}</Button>
            <Button variant="primary" disabled={!valid || mutacion.isPending} onClick={submit}>
              {mutacion.isPending
                ? t(editando ? 'common.state.saving' : 'companies.form.creating')
                : t(editando ? 'common.action.save' : 'common.action.create')}
            </Button>
          </span>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (valid) submit()
        }}
      >
        <Field label={t('common.label.name')} htmlFor="company-name">
          <Input
            id="company-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </Field>

        {/* El slug identifica a la empresa en URLs y en el canal de Slack: se
            elige al darla de alta y después no se toca. */}
        {editando ? null : (
          <Field label="Slug" htmlFor="company-slug" hint={t('companies.form.slugHint')}>
            <Input
              id="company-slug"
              value={slugFinal}
              onChange={(event) => {
                setSlugTocado(true)
                setSlug(slugify(event.target.value))
              }}
            />
          </Field>
        )}

        {editando ? (
          <Field label={t('common.label.status')} htmlFor="company-status">
            <Select
              id="company-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as CompanyStatus)}
            >
              <option value="active">{t('companies.status.active')}</option>
              <option value="paused">{t('companies.status.paused')}</option>
              <option value="archived">{t('companies.status.archived')}</option>
            </Select>
          </Field>
        ) : null}

        <Field label={t('common.label.owner')} htmlFor="company-owner">
          <Select
            id="company-owner"
            value={ownerId}
            onChange={(event) => setOwnerId(event.target.value)}
          >
            <option value="">{t('common.label.noOwner')}</option>
            {[...users.values()].map((user) => (
              <option key={user.id} value={user.id}>
                {user.display_name}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('companies.form.hue')}
          htmlFor="company-hue"
          hint={t('companies.form.hueHint')}
        >
          <span className="flex items-center gap-2">
            <Input
              id="company-hue"
              type="range"
              min={0}
              max={359}
              value={hue}
              onChange={(event) => setHue(event.target.value)}
            />
            {/* La muestra usa el mismo token que la espina de las filas. */}
            <span
              aria-hidden="true"
              className="h-6 w-1 shrink-0 rounded-full"
              style={{ background: `hsl(${hue} 70% 50%)` }}
            />
            <span className="num w-8 text-2xs text-text-3">{hue}</span>
          </span>
        </Field>

        <Field
          label={t('companies.form.slack')}
          htmlFor="company-slack"
          hint={t('companies.form.slackHint')}
        >
          <Input
            id="company-slack"
            value={slack}
            onChange={(event) => setSlack(event.target.value)}
            placeholder="C01ABCDEF"
          />
        </Field>
      </form>
    </Sheet>
  )
}
